import {
    ButtonHTMLAttributes,
    CSSProperties,
    DetailedHTMLProps,
    KeyboardEvent,
    RefObject,
    PropsWithChildren,
    useEffect,
    useRef,
    useState
} from "react";

import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import Video from "@/components/wpl/video/src";
import { Modal } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM
} from "@/libraries/design/foundations/grid/src/base";

import {
    Button,
    ButtonProps,
    ButtonVariant
} from "../../../components/button/src/button";
import { Link, LinkProps } from "../../../components/link/src/link";

type PrimaryCallToActionProps = {
    text: string;
} & ButtonProps;

export type HeroBackgroundColor = "black" | "blue" | "neutral";
type TitleAs = "h1" | "h2";

type PanelConfig = {
    title: string;
    body?: string;
    primaryCallToAction?: PrimaryCallToActionProps;
    secondaryCallToAction?: LinkProps;
    bgColor: HeroBackgroundColor;
    bgImage?: {
        lg: string;
    };
    layout?: "center" | "start" | "end";
};

export type TabDataProps = {
    id: string;
    eyebrow?: string;
    title: string;
    panel: PanelConfig;
};
export type PanelContainerProps = PropsWithChildren;
export type PanelProps = {
    isSelected: boolean;
    id: string;
    titleAs: TitleAs;
    panel: PanelConfig;
};
export type TabListProps = PropsWithChildren<{
    listRef: RefObject<HTMLElement | null>;
    isTabListViewable: boolean;
}>;
export type TabProps = PropsWithChildren<{
    isSelected: boolean;
    id: string;
}> &
    DetailedHTMLProps<
        ButtonHTMLAttributes<HTMLButtonElement>,
        HTMLButtonElement
    >;

export type HeroCarouselProps = PropsWithChildren<{
    readonly tabsData: TabDataProps[];
}>;

export const HeroCarousel = ({ tabsData }: HeroCarouselProps) => {
    const [selectedTab, setSelectedTab] = useState(0);
    const listRef = useRef<HTMLElement>(null);

    const { ref: lastTabRef, inView: isTabListEndVisible } = useInView({
        root: listRef?.current,
        threshold: 1
    });
    const { ref: firstTabRef, inView: isTabListStartVisible } = useInView({
        root: listRef?.current,
        threshold: 1
    });

    useEffect(() => {
        const listElement = listRef?.current;
        if (listElement) {
            const tabElement: Element | undefined =
                listElement.children?.[selectedTab + 1];
            if (tabElement && typeof tabElement.scrollIntoView === "function") {
                (tabElement as HTMLButtonElement).scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "end"
                });
                (tabElement as HTMLButtonElement).focus();
            }
        }
    }, [selectedTab]);

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
            let newFocusIndex = 0;
            if (e.key === "ArrowRight") {
                newFocusIndex = selectedTab + 1;
                if (newFocusIndex >= tabsData.length) {
                    newFocusIndex = 0;
                }
            } else if (e.key === "ArrowLeft") {
                newFocusIndex = selectedTab - 1;
                // If we're at the start, move to the end
                if (newFocusIndex < 0) {
                    newFocusIndex = tabsData.length - 1;
                }
            }

            setSelectedTab(newFocusIndex);
        }
    };

    return (
        <section
            id="hero-carousel"
            data-testid="hero-carousel"
            css={sectionStyles}
        >
            <div
                css={css({
                    display: "flex",
                    flexDirection: "column-reverse",
                    width: "100%",
                    overflow: "hidden"
                })}
            >
                <TabList
                    listRef={listRef}
                    isTabListViewable={
                        isTabListStartVisible && isTabListEndVisible
                    }
                >
                    <span ref={firstTabRef} />
                    {tabsData.map((tab, i) => (
                        <Tab
                            key={tab.id}
                            isSelected={i === selectedTab}
                            id={tab.id}
                            onClick={() => {
                                setSelectedTab(i);
                            }}
                            onKeyDown={onKeyDown}
                        >
                            {tab.eyebrow && (
                                <span className="eyebrow-sm" css={tabTextStyle}>
                                    {tab.eyebrow}
                                </span>
                            )}
                            <span css={[tabTextStyle, tabTitleStyle]}>
                                {tab.title}
                            </span>
                        </Tab>
                    ))}
                    <span ref={lastTabRef} />
                </TabList>
                <PanelContainer>
                    {tabsData.map((tab, i) => (
                        <Panel
                            key={tab.id}
                            isSelected={i === selectedTab}
                            id={tab.id}
                            panel={tab.panel}
                            titleAs={i === 0 ? "h1" : "h2"}
                        />
                    ))}
                </PanelContainer>
            </div>
        </section>
    );
};

export default HeroCarousel;

const TabList = ({ children, listRef, isTabListViewable }: TabListProps) => {
    return (
        <div css={tabListStyle}>
            <div
                css={[scrollableStyle, isTabListViewable && fittableStyle]}
                // @ts-ignore
                ref={listRef}
                tabIndex={-1}
                role="tablist"
            >
                {children}
            </div>
        </div>
    );
};
const Tab = ({ children, isSelected, id, onClick, onKeyDown }: TabProps) => {
    return (
        <button
            type="button"
            tabIndex={isSelected ? 0 : -1}
            role="tab"
            css={[tabStyle, isSelected && tabSelectedStyle]}
            aria-selected={isSelected ? "true" : "false"}
            aria-controls={`hero-panel-${id}`}
            id={`hero-tab-${id}`}
            onClick={onClick}
            onKeyDown={onKeyDown}
            data-label={`hero-tab-${id}`}
        >
            {children}
        </button>
    );
};
const PanelContainer = ({ children }: PanelContainerProps) => {
    return <div css={panelContainerStyle}>{children}</div>;
};
const Panel = ({ isSelected, id, panel, ...props }: PanelProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const baseStyle = css({
        "::before": {
            content: "",
            zIndex: "-1",
            position: "absolute",
            inset: 0,
            backgroundColor: "var(--bg-color, transparent)"
        },
        "::after": {
            content: "",
            zIndex: "-1",
            position: "absolute",
            insetBlock: 0,
            width: "100%",
            maxWidth: "1600px",
            justifySelf: "center",
            backgroundColor: "var(--bg-color, transparent)",
            [media.above.md]: {
                backgroundImage: panel.bgImage?.lg
                    ? `url(${panel.bgImage.lg})`
                    : undefined
            },
            backgroundSize: "cover",
            backgroundPosition:
                panel.layout === "end" ? "0% 50%" : "center center"
        }
    });

    const isVideoLink = panel.secondaryCallToAction?.type === "video";

    const isForegroundLight = ["blue", "black"].includes(panel.bgColor);

    return (
        <div
            role="tabpanel"
            id={`hero-panel-${id}`}
            data-testid={`hero-panel-${id}`}
            aria-hidden={!isSelected}
            css={[panelStyle, isSelected && panelSelectedStyle, baseStyle]}
            aria-labelledby={`hero-tab-${id}`}
            style={
                {
                    "--bg-color":
                        panel.bgColor === "neutral"
                            ? wacToken(`color.neutral.light200`)
                            : wacToken(`color.primary.${panel.bgColor}`)
                } as CSSProperties
            }
        >
            {isVideoLink && (
                <Modal
                    isOpen={isModalOpen}
                    closeModal={() => {
                        setIsModalOpen(false);
                    }}
                    closeLabel={"Close video"}
                >
                    <Video
                        muted={false}
                        videoType="mp4"
                        videoLink={panel.secondaryCallToAction?.href as string}
                    />
                </Modal>
            )}
            <div
                css={[
                    panelDetailsStyle,
                    isForegroundLight && panelColorStyle,
                    panel.layout === "end"
                        ? panelEndStyle
                        : panel.layout === "start"
                        ? panelStartStyle
                        : panelCenteredStyle
                ]}
            >
                <PanelContent panel={panel} titleAs={props.titleAs} />
                <PanelActions
                    {...panel}
                    onVideoClick={
                        isVideoLink
                            ? (e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsModalOpen(true);
                              }
                            : undefined
                    }
                />
            </div>
        </div>
    );
};
const PanelContent = ({
    panel,
    ...props
}: {
    panel: PanelConfig;
    titleAs: TitleAs;
}) => {
    const { titleAs: Title = "h2" } = props;

    return (
        <>
            <Title className="h1" css={detailsTextStyle}>
                {panel.title}
            </Title>
            {panel.body && (
                <p className="body-lg" css={detailsTextStyle}>
                    {panel.body}
                </p>
            )}
        </>
    );
};
const PanelActions = (
    panel: PanelConfig & { onVideoClick?: (e: React.MouseEvent) => void }
) => {
    return (
        panel && (
            <div
                css={[
                    actionContainerStyle,
                    (panel.layout === "start" || panel.layout === "end") &&
                        actionStartStyle
                ]}
            >
                {panel.primaryCallToAction && (
                    <PrimaryCallToAction
                        bgColor={panel.bgColor}
                        {...panel.primaryCallToAction}
                    />
                )}
                {panel.secondaryCallToAction && (
                    <SecondaryCallToAction {...panel} />
                )}
            </div>
        )
    );
};
const PrimaryCallToAction = (
    props: PrimaryCallToActionProps & { bgColor: HeroBackgroundColor }
) => {
    const { bgColor, ...rest } = props;
    let variant = "primary";
    if (bgColor === "blue") {
        variant = "primary_alt";
    }
    return (
        <Button
            {...rest}
            variant={variant as ButtonVariant}
            data-testid="primary-cta"
        >
            {props.text}
        </Button>
    );
};
const SecondaryCallToAction = (
    panel: PanelConfig & { onVideoClick?: (e: React.MouseEvent) => void }
) => {
    const { secondaryCallToAction } = panel;
    const bgColor = ["blue", "black"].includes(panel.bgColor)
        ? "bgBlackBlue"
        : "bgWhite";
    return (
        // @ts-ignore
        <Link
            {...secondaryCallToAction}
            bgColor={bgColor}
            anchorProps={{ "data-testid": "secondary-cta" }}
            onClick={panel.onVideoClick}
            onKeyDown={panel.onVideoClick}
            role="button"
            tabIndex={0}
        >
            {secondaryCallToAction?.text}
        </Link>
    );
};

const sectionStyles = css({
    width: "100%",
    overflow: "hidden"
});
const tabListStyle = css({
    background: wacToken("color.primary.black"),
    width: "100%",
    color: wacToken("color.neutral.light0"),
    overflow: "hidden",
    paddingInline: WCL_GRID_PADDING_SM,
    [media.above.sm]: {
        paddingInline: WCL_GRID_PADDING_LG
    }
});
const scrollableStyle = css({
    "minHeight": "5.5rem",
    "display": "flex",
    "listStyle": "none",
    "marginBlock": 0,
    "marginInline": "auto",
    "paddingInline": 0,
    "paddingBlock": `0 ${token("space.250")}`,
    "overflowX": "scroll",
    "scrollSnapType": "x mandatory",
    [media.above.sm]: {
        maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
    },
    "@supports (scrollbar-width: none)": {
        paddingBlockEnd: token("space.0"),
        scrollbarWidth: "none"
    }
});
const fittableStyle = css({
    justifyContent: "center"
});
const tabStyle = css({
    "borderBlockStart": `${token("space.100")} solid transparent`,
    "transition": "border-color 125ms ease-out",
    "borderBlockEnd": "none",
    "borderInline": "none",
    "appearance": "none",
    "background": "transparent",
    "fontFamily": "inherit",
    "fontSize": "1rem",
    "width": "16rem",
    "minWidth": "16rem",
    "cursor": "pointer",
    "scrollSnapAlign": "start",
    "paddingBlock": token("space.100"),
    "paddingInline": token("space.300"),
    "alignItems": "start",
    "display": "flex",
    "flexDirection": "column",
    "gap": token("space.075"),
    "&:hover, &:active": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        span: {
            opacity: 1
        }
    }
});
const tabTextStyle = css({
    color: wacToken("color.neutral.light0"),
    textAlign: "start",
    margin: 0,
    opacity: 0.5,
    transition: "opacity 125ms ease-out"
});

const tabTitleStyle = css({
    color: wacToken("color.neutral.light0"),
    fontSize: "1rem",
    fontWeight: "600",
    lineHeight: "1.25"
});
const tabSelectedStyle = css({
    borderBlockStart: `${token("space.100")} solid ${wacToken(
        "color.primary.saffron"
    )}`,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        opacity: 1
    }
});
const panelContainerStyle = css({
    display: "grid"
});
const panelStyle = css({
    transition: "opacity 175ms linear",
    gridColumn: "1 / 2",
    gridRow: "1 / 2",
    display: "grid",
    opacity: 0,
    zIndex: "-1",
    position: "relative"
});
const panelSelectedStyle = css({
    opacity: 1,
    zIndex: "1"
});
const panelDetailsStyle = css({
    "gridColumn": "1 / 2",
    "gridRow": "1 / 2",
    "paddingInline": WCL_GRID_PADDING_SM,
    "paddingBlock": token("space.800"),
    "display": "grid",
    "margin": "auto",
    "gap": token("space.300"),

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> *": {
        textAlign: "center",
        placeItems: "center"
    },

    [media.above.sm]: {
        paddingInline: WCL_GRID_PADDING_LG
    },
    [media.above.md]: {
        gridTemplateColumns: "repeat(12, 1fr)",
        gap: token("space.400"),
        maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
    }
});
const panelEndStyle = css({
    [media.above.md]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "7 / -1",
            textAlign: "start",
            placeItems: "start"
        }
    }
});
const panelStartStyle = css({
    [media.above.md]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "1 / 7",
            textAlign: "start",
            placeItems: "start"
        }
    }
});
const panelCenteredStyle = css({
    [media.above.md]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "3 / span 8"
        }
    }
});
const detailsTextStyle = css({
    margin: 0
});
const panelColorStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p, h2": {
        color: `${wacToken("color.neutral.light0")} !important`
    }
});
const actionContainerStyle = css({
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: token("space.300")
});
const actionStartStyle = css({
    [media.above.md]: {
        justifyContent: "start"
    }
});
